import * as THREE from 'three';
import { HealthBar } from '../../HealthBar.js';
import { colored } from '../../Style.js';
import {
  NAVY, paint, foamMaterial, smokeMaterial, bake, slab, flagGeometry, waveFlag, sailor, HATS, Buoyancy
} from './units-style.js';

// Barge plan: a chamfered rectangle that stays within 0.95 of the 2.0 collision radius
const HALF_X = 1.12;
const HALF_Z = 1.4;
const CHAMFER = 0.42;
const DECK_Y = 0.42;
const MORTAR_TILT = 0.6;

function outline(grow = 0) {
  const x = HALF_X + grow;
  const z = HALF_Z + grow;
  const c = CHAMFER;
  return [[-x + c, -z], [x - c, -z], [x, -z + c], [x, z - c], [x - c, z], [-x + c, z], [-x, z - c], [-x, -z + c]];
}

// Mortar Barge (rocket launcher): a broad flat barge with team-painted sides
// carrying a squat heavy mortar on a turntable, a two-man crew, a stack of
// shells and the team flag. The mortar lobs with a recoil and a smoke puff.
export class RocketLauncher {
  constructor(scene, unit, color) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.mesh = null;
    this.lastPosition = null;
    this.targetRotation = 0;
    this.healthBar = null;
    this.maxHealth = 20; // RocketLauncherHealth from server
    this.geometries = [];
    this.materials = [];
    this.recoil = 0;
    this.create();
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  part(group, parts) {
    const mesh = new THREE.Mesh(this.track(bake(parts)), paint());
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    return mesh;
  }

  create() {
    const bodyGroup = new THREE.Group();
    const team = this.color;

    this.model = new THREE.Group();
    bodyGroup.add(this.model);

    // Hull in bands: antifouling below the water, then team paint up to a proud rubbing strake
    const hull = [
      colored(slab(outline(-0.12), -0.32, 0), NAVY.bottom),
      colored(slab(outline(), 0, 0.3), team),
      colored(slab(outline(0.03), 0.3, 0.4), team),
      colored(slab(outline(-0.08), 0.36, DECK_Y), NAVY.deck)
    ];
    // Deck planks and white corner bollards
    for (let i = -3; i <= 3; i++) {
      hull.push(colored(new THREE.BoxGeometry(0.03, 0.01, HALF_Z * 1.8), NAVY.deckDark, { x: i * 0.3, y: DECK_Y + 0.005 }));
    }
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        hull.push(colored(new THREE.CylinderGeometry(0.07, 0.08, 0.2, 6), NAVY.white, { x: sx * 0.85, y: DECK_Y + 0.1, z: sz * 1.12 }));
      }
    }
    // Mortar turntable and its iron bed
    hull.push(colored(new THREE.CylinderGeometry(0.7, 0.78, 0.16, 10), NAVY.iron, { y: DECK_Y + 0.08, z: 0.15 }));
    hull.push(colored(new THREE.CylinderGeometry(0.72, 0.72, 0.05, 10), NAVY.brass, { y: DECK_Y + 0.17, z: 0.15 }));
    for (const sx of [-1, 1]) {
      hull.push(colored(new THREE.BoxGeometry(0.14, 0.42, 0.62), NAVY.woodDark, { x: sx * 0.42, y: DECK_Y + 0.38, z: 0.15 }));
    }
    // Shell pyramid, powder kegs and the flagstaff
    const shells = [[0, 0], [0.22, 0], [0.11, 0.19], [0.11, 0.07]];
    shells.forEach(([dx, dz], i) => {
      hull.push(colored(new THREE.SphereGeometry(0.11, 6, 4), NAVY.iron, { x: -0.7 + dx, y: DECK_Y + 0.11 + (i === 3 ? 0.17 : 0), z: -0.75 + dz }));
    });
    for (const dx of [0, 0.26]) {
      hull.push(colored(new THREE.CylinderGeometry(0.12, 0.12, 0.3, 8), NAVY.wood, { x: 0.5 + dx, y: DECK_Y + 0.15, z: -0.95 }));
      hull.push(colored(new THREE.CylinderGeometry(0.125, 0.125, 0.04, 8), NAVY.iron, { x: 0.5 + dx, y: DECK_Y + 0.22, z: -0.95 }));
    }
    hull.push(colored(new THREE.CylinderGeometry(0.035, 0.045, 1.9, 5), NAVY.woodDark, { x: -0.75, y: DECK_Y + 0.95, z: -0.85 }));
    // Crew: a gunner with a rammer and a loader
    hull.push(...sailor({ coat: NAVY.coat, hat: HATS.cap, scale: 1.1, x: 0.75, y: DECK_Y, z: 0.65, ry: -Math.PI / 2 }));
    hull.push(...sailor({ coat: NAVY.coat, hat: HATS.cap, scale: 1.1, x: -0.15, y: DECK_Y, z: -0.85, ry: 0.4 }));
    hull.push(colored(new THREE.CylinderGeometry(0.025, 0.025, 1.0, 4), NAVY.woodLight, { x: 0.62, y: DECK_Y + 0.7, z: 0.65, rz: 0.4 }));
    this.part(this.model, hull);

    // The mortar: a squat iron bore leaning forward, which sinks back on firing
    this.mortar = new THREE.Group();
    this.mortar.position.set(0, DECK_Y + 0.5, 0.15);
    this.mortar.rotation.x = MORTAR_TILT;
    this.model.add(this.mortar);
    this.recoilGroup = new THREE.Group();
    this.mortar.add(this.recoilGroup);
    this.part(this.recoilGroup, [
      colored(new THREE.SphereGeometry(0.42, 8, 6), NAVY.iron, { y: 0.0 }),
      colored(new THREE.CylinderGeometry(0.1, 0.1, 0.96, 6), NAVY.iron, { rz: Math.PI / 2 }),
      colored(new THREE.CylinderGeometry(0.36, 0.42, 0.75, 10), NAVY.iron, { y: 0.4 }),
      colored(new THREE.CylinderGeometry(0.43, 0.43, 0.08, 10), NAVY.brass, { y: 0.22 }),
      colored(new THREE.CylinderGeometry(0.43, 0.38, 0.14, 10), NAVY.brass, { y: 0.8 }),
      colored(new THREE.CylinderGeometry(0.26, 0.26, 0.02, 10), NAVY.hat, { y: 0.88 }),
      colored(new THREE.BoxGeometry(0.3, 0.2, 0.06), team, { y: 0.45, z: 0.39 })
    ]);

    // Smoke puff from the muzzle, only after a real shot
    this.puffs = [];
    for (let i = 0; i < 3; i++) {
      const puff = new THREE.Mesh(this.track(new THREE.IcosahedronGeometry(0.32, 0)), smokeMaterial());
      puff.visible = false;
      puff.userData.offset = new THREE.Vector3((i - 1) * 0.18, 0, (i % 2) * 0.12);
      this.model.add(puff);
      this.puffs.push(puff);
    }

    // Team flag
    this.flagMaterial = new THREE.MeshStandardMaterial({ color: team, roughness: 0.9, flatShading: true, side: THREE.DoubleSide });
    this.materials.push(this.flagMaterial);
    this.flag = new THREE.Mesh(this.track(flagGeometry(0.62, 0.42)), this.flagMaterial);
    this.flag.position.set(-0.75, DECK_Y + 1.88, -0.85);
    this.flag.rotation.y = Math.PI / 2;
    this.flag.castShadow = true;
    this.model.add(this.flag);

    // Foam collar around the barge and a little wash astern
    this.water = new THREE.Group();
    bodyGroup.add(this.water);
    const foam = [colored(slab(outline(0.14), 0.02, 0.03), 0xffffff)];
    foam.push(colored(new THREE.BoxGeometry(1.0, 0.01, 0.2), 0xffffff, { y: 0.03, z: -HALF_Z - 0.2 }));
    this.wake = new THREE.Mesh(this.track(bake(foam)), foamMaterial());
    this.wake.renderOrder = 1;
    this.water.add(this.wake);

    // Create health bar
    this.healthBar = new HealthBar(this.maxHealth, 2.6, 0.28);
    this.healthBar.getGroup().position.y = 2.9;
    bodyGroup.add(this.healthBar.getGroup());

    this.mesh = bodyGroup;

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };
    this.buoyancy = new Buoyancy(this.mesh, this.model, { bob: 0.04, roll: 0.025, pitch: 0.03, rate: 1.1, topSpeed: 3.5 });
    this.buoyancy.update();

    this.scene.add(this.mesh);
  }

  updatePosition(position) {
    if (this.mesh) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          this.targetRotation = Math.atan2(dx, dz);
        }
      }

      // Smoothly interpolate rotation
      let rotationDiff = this.targetRotation - this.mesh.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.mesh.rotation.y += rotationDiff * 0.1;

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };

      this.animate();
    }
  }

  // Floating, foam and flag; recoil and smoke follow a real shot
  animate() {
    const dt = this.buoyancy.update();
    const speed = this.buoyancy.speed;
    this.wake.visible = speed > 0.08;
    const t = performance.now() / 1000;
    waveFlag(this.flag, t + this.buoyancy.phase, 0.1);
    if (dt <= 0) return;

    // The bore slams back into its bed and the barge dips with it
    this.recoil = Math.max(0, this.recoil - dt * 1.2);
    const kick = Math.max(0, this.recoil - 0.6) / 0.4;
    this.recoilGroup.position.y = -0.22 * kick * kick;
    this.model.position.y -= 0.08 * kick;

    // A puff rolls out of the muzzle, rises and thins away
    const age = 1 - this.recoil;
    for (const puff of this.puffs) {
      puff.visible = this.recoil > 0;
      if (!puff.visible) continue;
      const o = puff.userData.offset;
      puff.position.set(o.x, DECK_Y + 1.3 + age * 1.0, 0.15 + Math.sin(MORTAR_TILT) * 0.8 + o.z - age * 0.3);
      puff.scale.setScalar(Math.sin(Math.min(1, age * 1.4) * Math.PI) * 1.3 + 0.05);
    }
  }

  // Called when the mortar actually fires
  fire() {
    this.recoil = 1;
  }

  // Set target position for aiming
  setTarget(targetPosition) {
    if (this.mesh && targetPosition) {
      const dx = targetPosition.x - this.mesh.position.x;
      const dz = targetPosition.z - this.mesh.position.z;
      this.targetRotation = Math.atan2(dx, dz);
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    // Shared materials stay; the flag material and all geometry are ours
    this.geometries.forEach(g => g.dispose());
    this.materials.forEach(m => m.dispose());
    this.geometries = [];
    this.materials = [];
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
  }
}
